import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { useSnackbar } from "notistack";
import { formatarData, formatarValor } from "../components/formatarValores";
import ItensComprados from "./ItensComprados";
import ReactGA from "react-ga4";
import { useCart } from "../context/CartContext";
import { useLanguage } from "../context/LanguageContext";
import { isImpersonating } from "../utils/impersonation";

function NovoPagamento() {
  const { t } = useLanguage();
  const isSimulating = isImpersonating();
  const [compra, setCompra] = useState(null);
  const [qrCodeSrc, setQrCodeSrc] = useState("");
  const idCompra = localStorage.getItem("idCompra");
  const { clearCart } = useCart();
  const user = JSON.parse(localStorage.getItem("user"));

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  //GA
  useEffect(() => {
    if (localStorage.getItem("adminImpersonation")) return;
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "tela_de_pagamento",
    });

    ReactGA.event({
      category: "Início de pagamento",
      action: "chegou_ao_pix",
      label: user.nome, // optional
    });
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/compras/${idCompra}`
        );
        clearCart();
        setCompra(response.data); // Atualizando o estado com os dados da compra
      } catch (error) {
        console.error("Erro ao buscar compra:", error);
      }
    };

    fetchData();
  }, [idCompra]);

  useEffect(() => {
    if (compra) {
      setQrCodeSrc(compra.chavePix);
    }
  }, [compra]);

  const atualizarStatus = () => {
    const novoStatus = {
      status: "Pagamento em análise",
    };
    axios
      .put(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/compras/${idCompra}`,
        novoStatus
      )
      .catch((error) => {
        enqueueSnackbar("Não foi possível atualizar", { variant: "error" });
        console.log(error);
      });
  };

  const onSuccessCopyPix = () => {
    enqueueSnackbar(t("payment.copiedSuccess"), {
      variant: "success",
    });
    atualizarStatus();
    setTimeout(() => {
      navigate("/ar/compras");
    }, 1000);
  };

  const fallbackCopyPix = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        onSuccessCopyPix();
      } else {
        enqueueSnackbar(t("payment.copyFail"), {
          variant: "warning",
        });
      }
    } catch (err) {
      console.error("Fallback copy error:", err);
      enqueueSnackbar(t("payment.copyFail"), { variant: "error" });
    }
  };

  const copiarParaClipboard = () => {
    if (!qrCodeSrc) {
      enqueueSnackbar("Chave Pix ainda não carregou", { variant: "warning" });
      return;
    }

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(qrCodeSrc)
        .then(() => {
          onSuccessCopyPix();
        })
        .catch(() => {
          fallbackCopyPix(qrCodeSrc);
        });
    } else {
      fallbackCopyPix(qrCodeSrc);
    }
  };
  const gerarChavePIX = async () => {
    const data = {
      codigo: compra?._id ? `${compra._id}-${Date.now()}` : `${user.id}-${Date.now()}`,
      valor: compra.totalPrice,
    };

    ReactGA.event({
      category: "Início de pagamento",
      action: "valor_do_pix",
      value: compra ? compra.totalPrice : null,
    });
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/compras/pagamento/gerarPayload`,
        data
      );

      const novaChave = response.data.chavePix;
      setQrCodeSrc(novaChave);
      setCompra((prev) => (prev ? { ...prev, chavePix: novaChave } : prev));
      salvarNovoQr(novaChave);
      return response.data;
    } catch (error) {
      console.error("Problemas na geração do QRCODE", error);
    }
  };

  const salvarNovoQr = (novoQr) => {
    const data = {
      chavePix: novoQr,
    };
    axios
      .put(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/compras/${idCompra}`,
        data
      )
      .then((response) => {})
      .catch((error) => {
        enqueueSnackbar("Não foi possível atualizar", { variant: "error" });
        console.log(error);
      });
  };

  const getStatusText = (status) => {
    if (!status) return t("common.loading");
    if (status === "Pendente") return t("payment.statusPending");
    if (status === "Pagamento em análise") return t("payment.statusInReview");
    if (status === "Pago") return t("payment.statusPaid");
    return status;
  };

  return (
    <div className={`${isSimulating ? "mt-32 sm:mt-36" : "mt-24"} px-4 transition-all`}>
      <div className="text-center text-black text-2xl mb-4">
        {t("payment.infoTitle")}
      </div>
      <div className="flex text-black justify-center py-0">
        <div className="w-full max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
            <div className="card bg-base-100 p-4 shadow-md">
              <div className="flex flex-col gap-y-1">
                <div className="flex items-center">
                  <label className="pr-1 text-[16px]">{t("payment.totalPhotos")} </label>
                  <div className="text-base">
                    {compra ? compra.quantity : t("common.loading")}
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="pr-2 text-[16px]">{t("payment.purchasedAt")}</label>
                  <div className="text-base">
                    {compra ? formatarData(compra.dataCompra) : t("common.loading")}
                  </div>
                </div>

                <div className="flex items-center">
                  <label className="pr-2 text-[16px]">{t("common.subtotal")}</label>
                  <span className="text-[16px] pr-1">R$</span>
                  <span className="text-[20px] py-0">
                    <strong className="py-0">
                      {compra
                        ? compra.subTotalInCents
                          ? formatarValor(compra.subTotalInCents / 100)
                          : formatarValor(compra.totalPrice)
                        : t("common.loading")}
                    </strong>
                  </span>
                </div>

                <div className="flex items-center">
                  <label className="pr-2 text-[16px]">{t("common.discount")}</label>
                  <span className="text-[16px] pr-1">R$</span>
                  <span className="text-[20px] py-0">
                    <strong className="py-0">
                      {compra
                        ? compra.discountAmountInCents
                          ? formatarValor(compra.discountAmountInCents / 100)
                          : formatarValor(0)
                        : t("common.loading")}
                    </strong>
                  </span>
                  {compra && compra.discountPercent ? (
                    <span className="ml-2 opacity-70">({compra.discountPercent}%)</span>
                  ) : null}
                </div>

                <div className="flex items-center">
                  <label className="pr-2 pt-3 text-[16px]">{t("common.total")} </label>
                  <span className="text-[16px] pt-3 pr-1">R$</span>{" "}
                  <span className="text-[50px] py-0">
                    <strong className="py-0">
                      {compra ? formatarValor(compra.totalPrice) : t("common.loading")}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
            <div className="card bg-base-100 p-4 shadow-md">
              <div className="flex-col items-center">
                <div>
                  <div className="flex justify-between items-center">
                    <label className="pr-2 text-[16px]">{t("payment.pixCopyPaste")}</label>
                    <span
                      onClick={copiarParaClipboard}
                      className="btn btn-sm btn-primary"
                    >
                      {t("payment.copyPixKey")}
                    </span>
                  </div>
                  <textarea
                    id="chavePixInput"
                    className="w-full p-2 bg-base-200 rounded-md"
                    rows={5}
                    value={qrCodeSrc || t("payment.generatingPix")}
                    readOnly
                  />
                </div>
                <div className="flex justify-center py-3">
                  <div className="bg-white p-4 rounded-md">
                    <QRCode key={qrCodeSrc} value={qrCodeSrc || "Gerando..."} size={220} />
                   </div>
                 </div>
                 <div className="flex justify-center">
                   <button onClick={gerarChavePIX} className="btn btn-outline" disabled={!compra}>
                     {t("payment.generateAgain")}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>
      <div className="flex justify-center">
        <strong className="align-middle pt-1 pr-1">{t("payment.status")} </strong>
        <div
          className={
            (compra && compra.status === "Pendente") || "Em processamento"
              ? "text-orange-500 text-[20px]"
              : "text-green-500 text-[20px]"
          }
        >
          {compra ? getStatusText(compra.status) : t("common.loading")}
        </div>
      </div>
      {compra && compra.status === "Pago" ? null : (
        <>
          {compra ? (
            <div className=" text-gray-500 flex justify-center flex-col mt-4 max-w-xl mx-auto">
              <div>
                <label className="font-semibold text-base-content">{t("payment.instructionsTitle")}</label>
                <ul className="mt-1 space-y-1">
                  <li>{t("payment.instruction1")}</li>
                  <li>{t("payment.instruction2")}</li>
                  <li>{t("payment.instruction3")}</li>
                </ul>
              </div>

              {t("payment.customPaymentNote") ? (
                <div className="mt-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-xs sm:text-sm text-base-content/85">
                  <span className="font-bold text-blue-600 dark:text-blue-400 block mb-1">
                    ℹ️ Information / Info:
                  </span>
                  <p>{t("payment.customPaymentNote")}</p>
                </div>
              ) : null}

              <div className="flex flex-col items-center my-4 ">
                <div className="flex items-center">
                  <label className="pr-2 text-[16px]">{t("common.total")}</label>
                  <span className="text-[16px] pr-1">R$</span>
                  <label className="text-[16px] pr-1">
                    {compra ? formatarValor(compra.totalPrice) : t("common.loading")}
                  </label>
                </div>
              </div>
            </div>
          ) : null}
        </>
      )}
      <div className="flex justify-center mb-12">
        <div className="max-w-[1000px]">
          {compra && (
            <ItensComprados produtos={compra.produtos} pagamento={compra.status} />
          )}
        </div>
      </div>
    </div>
  );
}

export default NovoPagamento;
